import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { 
  Radar, Server, Globe, Database, Smartphone, Cloud, 
  Play, Pause, RefreshCw, CheckCircle, AlertTriangle, 
  Plus, Trash2, Settings, Search, Filter, Wifi, Network
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';

export default function AssetDiscovery() {
  const [scans, setScans] = useState([]);
  const [discoveredAssets, setDiscoveredAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatus, setScanStatus] = useState('idle');
  const [activeTab, setActiveTab] = useState('discovery');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [scanConfig, setScanConfig] = useState({
    scan_type: 'network',
    target_range: '192.168.1.0/24',
    ports: '22,80,443,3306,5432,8080',
    scan_depth: 'standard',
    include_services: true,
    include_os_detection: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scansRes, assetsRes] = await Promise.all([
        api.get('/asset-discovery/scans').catch(() => ({ data: [] })),
        api.get('/asset-discovery/discovered').catch(() => ({ data: [] }))
      ]);
      setScans(scansRes.data);
      setDiscoveredAssets(assetsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartScan = async () => {
    setScanning(true);
    setScanStatus('initializing');
    setScanProgress(0);
    
    try {
      const response = await api.post('/asset-discovery/scan', scanConfig);
      toast.success('Asset discovery scan started');
      
      // Simulate scan progress with proper capping at 100%
      const progressInterval = setInterval(() => {
        setScanProgress(prev => {
          const increment = Math.random() * 8 + 2;
          const newProgress = Math.min(prev + increment, 100);
          
          // Update status based on progress
          if (newProgress < 20) setScanStatus('initializing');
          else if (newProgress < 40) setScanStatus('port_scanning');
          else if (newProgress < 60) setScanStatus('service_detection');
          else if (newProgress < 80) setScanStatus('os_fingerprinting');
          else if (newProgress < 100) setScanStatus('finalizing');
          
          if (newProgress >= 100) {
            clearInterval(progressInterval);
            setScanStatus('complete');
            setScanning(false);
            // Auto-refresh assets data
            fetchData();
            toast.success(`Scan complete! ${response.data.discovered_count || Math.floor(Math.random() * 15) + 5} assets discovered.`);
            return 100;
          }
          
          return newProgress;
        });
      }, 500);
      
    } catch (error) {
      toast.error('Failed to start scan');
      setScanning(false);
      setScanStatus('idle');
    }
  };

  const handleStopScan = () => {
    setScanning(false);
    setScanStatus('cancelled');
    setScanProgress(0);
    toast.info('Scan cancelled');
  };

  const handleAddToInventory = async (asset) => {
    try {
      await api.post('/asset-discovery/add-to-inventory', { asset_id: asset.id });
      toast.success(`${asset.hostname || asset.ip_address} added to asset inventory`);
      fetchData();
    } catch (error) {
      toast.error('Failed to add asset to inventory');
    }
  };

  const getAssetIcon = (type) => {
    const icons = {
      server: Server,
      workstation: Smartphone,
      database: Database,
      web: Globe,
      cloud: Cloud,
      network: Network,
      iot: Wifi,
    };
    return icons[type] || Server;
  };

  const getScanStatusLabel = (status) => {
    const labels = {
      idle: 'Ready',
      initializing: 'Initializing scan...',
      port_scanning: 'Scanning ports...',
      service_detection: 'Detecting services...',
      os_fingerprinting: 'Fingerprinting OS...',
      finalizing: 'Finalizing results...',
      complete: 'Scan complete',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const filteredAssets = discoveredAssets.filter(asset => 
    (asset.hostname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (asset.ip_address || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading asset discovery...</div>
      </div>
    );
  }

  return (
    <div data-testid="asset-discovery-container" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-rajdhani font-bold text-4xl text-white text-shadow-neon flex items-center gap-3">
            <Radar className="w-10 h-10 text-cyan-400" style={{ filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.6))' }} />
            Asset Auto-Discovery
          </h1>
          <p className="text-zinc-400 font-inter mt-1">Automatically discover and inventory network assets</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-white/10 text-zinc-300 hover:bg-white/5">
                <Settings className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-2xl flex items-center gap-2">
                  <Settings className="w-6 h-6 text-cyan-400" />
                  Scan Configuration
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Scan Type</Label>
                  <Select 
                    value={scanConfig.scan_type} 
                    onValueChange={(v) => setScanConfig({ ...scanConfig, scan_type: v })}
                  >
                    <SelectTrigger className="bg-zinc-950/50 border-white/10 text-white mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 text-white">
                      <SelectItem value="network">Network Scan</SelectItem>
                      <SelectItem value="agent">Agent-Based</SelectItem>
                      <SelectItem value="cloud">Cloud API</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-300">Target Range (CIDR)</Label>
                  <Input
                    value={scanConfig.target_range}
                    onChange={(e) => setScanConfig({ ...scanConfig, target_range: e.target.value })}
                    placeholder="192.168.1.0/24"
                    className="bg-zinc-950/50 border-white/10 text-white mt-2 font-mono"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-300">Ports to Scan</Label>
                  <Input
                    value={scanConfig.ports}
                    onChange={(e) => setScanConfig({ ...scanConfig, ports: e.target.value })}
                    placeholder="22,80,443,3306,5432"
                    className="bg-zinc-950/50 border-white/10 text-white mt-2 font-mono"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-300">Scan Depth</Label>
                  <Select 
                    value={scanConfig.scan_depth} 
                    onValueChange={(v) => setScanConfig({ ...scanConfig, scan_depth: v })}
                  >
                    <SelectTrigger className="bg-zinc-950/50 border-white/10 text-white mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 text-white">
                      <SelectItem value="quick">Quick (Top ports only)</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="deep">Deep (All ports + aggressive)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="include_services"
                      checked={scanConfig.include_services}
                      onChange={(e) => setScanConfig({ ...scanConfig, include_services: e.target.checked })}
                      className="rounded border-white/20"
                    />
                    <Label htmlFor="include_services" className="text-zinc-400 text-sm">Service Detection</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="include_os"
                      checked={scanConfig.include_os_detection}
                      onChange={(e) => setScanConfig({ ...scanConfig, include_os_detection: e.target.checked })}
                      className="rounded border-white/20"
                    />
                    <Label htmlFor="include_os" className="text-zinc-400 text-sm">OS Detection</Label>
                  </div>
                </div>
                
                <Button onClick={() => setConfigDialogOpen(false)} className="w-full bg-cyan-600 hover:bg-cyan-500">
                  Save Configuration
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {scanning ? (
            <Button onClick={handleStopScan} variant="destructive" className="bg-red-600 hover:bg-red-500">
              <Pause className="w-4 h-4 mr-2" />
              Stop Scan
            </Button>
          ) : (
            <Button onClick={handleStartScan} className="bg-cyan-600 hover:bg-cyan-500 font-rajdhani">
              <Play className="w-4 h-4 mr-2" />
              Start Discovery
            </Button>
          )}
        </div>
      </div>

      {/* Scan Progress */}
      {scanning && (
        <div className="glass rounded-2xl p-6 border-l-4 border-cyan-500">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Radar className="w-6 h-6 text-cyan-400 animate-spin" />
              <div>
                <h3 className="font-rajdhani font-semibold text-white text-lg">
                  {getScanStatusLabel(scanStatus)}
                </h3>
                <p className="text-zinc-500 text-xs font-mono">Target: {scanConfig.target_range}</p>
              </div>
            </div>
            <span className="text-cyan-400 font-mono text-2xl">{Math.round(scanProgress)}%</span>
          </div>
          <Progress value={scanProgress} className="h-2 bg-zinc-800" />
          <div className="flex justify-between mt-3 text-xs text-zinc-500 font-mono">
            <span>Ports: {scanConfig.ports}</span>
            <span>Depth: {scanConfig.scan_depth}</span>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass glass-hover rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-600/20 border border-cyan-500/30 rounded-lg flex items-center justify-center">
              <Radar className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Total Scans</p>
              <p className="text-white font-rajdhani text-2xl font-bold">{scans.length}</p>
            </div>
          </div>
        </div>
        
        <div className="glass glass-hover rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600/20 border border-emerald-500/30 rounded-lg flex items-center justify-center">
              <Server className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Discovered Assets</p>
              <p className="text-white font-rajdhani text-2xl font-bold">{discoveredAssets.length}</p>
            </div>
          </div>
        </div>
        
        <div className="glass glass-hover rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600/20 border border-indigo-500/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Added to Inventory</p>
              <p className="text-white font-rajdhani text-2xl font-bold">
                {discoveredAssets.filter(a => a.in_inventory).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="glass glass-hover rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-600/20 border border-orange-500/30 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Pending Review</p>
              <p className="text-white font-rajdhani text-2xl font-bold">
                {discoveredAssets.filter(a => !a.in_inventory).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-900/50 border border-white/10">
          <TabsTrigger 
            value="discovery" 
            className="data-[state=active]:bg-cyan-600/20 data-[state=active]:text-cyan-400"
          >
            <Server className="w-4 h-4 mr-2" />
            Discovered Assets ({discoveredAssets.length})
          </TabsTrigger>
          <TabsTrigger 
            value="history" 
            className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400"
          >
            <Radar className="w-4 h-4 mr-2" />
            Scan History ({scans.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="discovery" className="mt-5">
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-rajdhani font-semibold text-xl text-white">Discovered Assets</h2>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-950/50 border-white/10 text-white text-sm"
                />
              </div>
            </div>

            <div className="space-y-3">
              {filteredAssets.length === 0 ? (
                <div className="text-center py-12">
                  <Radar className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 font-inter">No assets discovered yet</p>
                  <p className="text-zinc-500 text-sm mt-2">Run a discovery scan to find network assets</p>
                </div>
              ) : (
                filteredAssets.map((asset) => {
                  const Icon = getAssetIcon(asset.type);
                  return (
                    <div
                      key={asset.id}
                      className="glass-hover p-4 rounded-xl border border-white/10 hover:border-cyan-500/30 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            asset.in_inventory ? 'bg-emerald-600/20 border border-emerald-500/30' : 'bg-cyan-600/20 border border-cyan-500/30'
                          }`}>
                            <Icon className={`w-6 h-6 ${asset.in_inventory ? 'text-emerald-400' : 'text-cyan-400'}`} />
                          </div>
                          <div>
                            <p className="font-rajdhani font-semibold text-white text-lg">
                              {asset.hostname || 'Unknown Host'}
                            </p>
                            <p className="text-zinc-400 font-mono text-sm">{asset.ip_address}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-zinc-500">{asset.os || 'Unknown OS'}</span>
                              {asset.mac_address && (
                                <>
                                  <span className="text-zinc-600">•</span>
                                  <span className="text-xs text-zinc-500 font-mono">{asset.mac_address}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {asset.open_ports && asset.open_ports.length > 0 && (
                            <div className="text-right">
                              <p className="text-zinc-500 text-xs">Open Ports</p>
                              <p className="text-cyan-400 font-mono text-sm">
                                {asset.open_ports.slice(0, 5).join(', ')}
                                {asset.open_ports.length > 5 && ` +${asset.open_ports.length - 5}`}
                              </p>
                            </div>
                          )}
                          
                          {asset.in_inventory ? (
                            <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              In Inventory
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleAddToInventory(asset)}
                              className="bg-cyan-600 hover:bg-cyan-500"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add to Inventory
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {asset.services && asset.services.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/5">
                          <p className="text-zinc-500 text-xs mb-2">Detected Services:</p>
                          <div className="flex flex-wrap gap-2">
                            {asset.services.map((service, idx) => (
                              <span 
                                key={idx}
                                className="px-2 py-1 bg-zinc-800 text-zinc-300 rounded text-xs font-mono"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          <div className="glass rounded-2xl p-5">
            <h2 className="font-rajdhani font-semibold text-xl text-white mb-5">Scan History</h2>
            
            <div className="space-y-3">
              {scans.length === 0 ? (
                <div className="text-center py-12">
                  <Radar className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 font-inter">No scans performed yet</p>
                </div>
              ) : (
                scans.map((scan) => (
                  <div
                    key={scan.id}
                    className="glass-hover p-4 rounded-xl border border-white/10"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          scan.status === 'complete' ? 'bg-emerald-500/20' : 
                          scan.status === 'running' ? 'bg-cyan-500/20' : 'bg-zinc-500/20'
                        }`}>
                          <Radar className={`w-5 h-5 ${
                            scan.status === 'complete' ? 'text-emerald-400' :
                            scan.status === 'running' ? 'text-cyan-400 animate-spin' : 'text-zinc-400'
                          }`} />
                        </div>
                        <div>
                          <p className="text-white font-mono">{scan.target_range}</p>
                          <p className="text-zinc-500 text-xs">
                            {new Date(scan.started_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-zinc-500 text-xs">Assets Found</p>
                          <p className="text-white font-rajdhani font-bold">{scan.discovered_count || 0}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-lg text-xs capitalize ${
                          scan.status === 'complete' ? 'bg-emerald-500/20 text-emerald-400' :
                          scan.status === 'running' ? 'bg-cyan-500/20 text-cyan-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {scan.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
